/**
 * AutoGlow · Advisor · Card primitives.
 * KpiCard · MetricCard · InsightCard · GrowthPlayCard · SeoCard · ContentCard.
 *
 * All pure presentational · zero business logic.
 */
import React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { CitationBadge, EffortPill, ImpactPill, PriorityPill, StatusBadge } from "./Badges";

// ---------------------------------------------------------------------------
// KpiCard · one-metric card · displays current → target with trend
// ---------------------------------------------------------------------------

function TrendIcon({ trend }) {
  if (trend === "up") return <TrendingUp className="h-3 w-3 text-emerald-500" aria-label="al alza" />;
  if (trend === "down") return <TrendingDown className="h-3 w-3 text-red-500" aria-label="a la baja" />;
  return <Minus className="h-3 w-3 text-[var(--ag-text-dim)]" aria-label="estable" />;
}

export function KpiCard({ kpi, testId }) {
  if (!kpi) return null;
  const calculable = kpi.is_calculable_now !== false;
  return (
    <div
      data-testid={testId || `kpi-${kpi.code}`}
      className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-3"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs uppercase tracking-wide text-[var(--ag-text-dim)]">
          {kpi.label || kpi.code}
        </div>
        <TrendIcon trend={kpi.trend} />
      </div>
      {calculable ? (
        <>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="text-2xl font-bold">{kpi.current_value ?? "—"}</span>
            {kpi.target_value != null && (
              <>
                <span className="text-[var(--ag-text-dim)]">→</span>
                <span className="text-sm font-medium text-emerald-500">{kpi.target_value}</span>
              </>
            )}
            <span className="ml-1 text-[10px] text-[var(--ag-text-dim)]">
              {kpi.unit}
            </span>
          </div>
          <div className="mt-0.5 text-[10px] text-[var(--ag-text-dim)]">
            {kpi.horizon_days ? `${kpi.horizon_days} días` : ""} · {kpi.pillar}
          </div>
        </>
      ) : (
        <div className="mt-2 text-xs text-[var(--ag-text-dim)]">
          {kpi.fallback_message || "Información insuficiente."}
        </div>
      )}
      {kpi.citation && <CitationBadge sources={[kpi.citation]} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MetricCard · generic single-value · used in totals/averages
// ---------------------------------------------------------------------------

export function MetricCard({ label, value, hint, citation, testId }) {
  return (
    <div
      data-testid={testId || `metric-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}
      className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-3"
    >
      <div className="text-[10px] uppercase tracking-wide text-[var(--ag-text-dim)]">{label}</div>
      <div className="mt-0.5 text-2xl font-bold">{value ?? "—"}</div>
      {hint && <div className="mt-0.5 text-[10px] text-[var(--ag-text-dim)]">{hint}</div>}
      {citation && <CitationBadge sources={[citation]} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// InsightCard · used for verdicts, summaries, narratives
// ---------------------------------------------------------------------------

export function InsightCard({ title, text, citations, tone = "info", testId }) {
  const TONE = {
    info: "border-[var(--ag-border)]",
    success: "border-emerald-500/40 bg-emerald-500/5",
    warning: "border-amber-500/40 bg-amber-500/5",
    danger: "border-red-500/40 bg-red-500/5",
  }[tone];
  return (
    <div
      data-testid={testId || "insight-card"}
      className={`rounded-lg border bg-[var(--ag-surface)] p-3 ${TONE}`}
    >
      {title && <div className="text-xs font-semibold text-[var(--ag-text)]">{title}</div>}
      {text && (
        <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-[var(--ag-text)]">
          {text}
        </p>
      )}
      {citations && citations.length > 0 && <CitationBadge sources={citations} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// GrowthPlayCard · AG-02 experiment card
// ---------------------------------------------------------------------------

export function GrowthPlayCard({ experiment: e, testId }) {
  if (!e) return null;
  return (
    <div
      data-testid={testId || `growth-play-${e.play_id}`}
      className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-mono text-[var(--ag-text-dim)]">{e.play_id}</span>
        <PriorityPill priority={e.priority} />
        <ImpactPill impact={e.impact_label} />
        <EffortPill effort={e.effort_label} />
      </div>
      <h4 className="mt-2 text-sm font-semibold text-[var(--ag-text)]">{e.title}</h4>
      <p className="mt-1 text-xs text-[var(--ag-text-dim)]">{e.hypothesis}</p>
      {e.success_metric && (
        <div className="mt-2 rounded border border-[var(--ag-border)] bg-[var(--ag-surface-2)] p-2 text-[11px] text-[var(--ag-text)]">
          <span className="font-medium">Métrica de éxito:</span> {e.success_metric}
        </div>
      )}
      {e.citation && <CitationBadge sources={[e.citation]} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SeoCard · AG-03 per-code SEO snapshot
// ---------------------------------------------------------------------------

export function SeoCard({ item, testId }) {
  if (!item) return null;
  return (
    <div
      data-testid={testId || `seo-${item.code}`}
      className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-3"
    >
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-mono text-[var(--ag-text-dim)]">{item.code}</span>
        <span className="text-xl font-bold">{item.score ?? "—"}</span>
      </div>
      <div className="text-xs text-[var(--ag-text)]">{item.name || item.title}</div>
      {item.label && (
        <div className="mt-1">
          <StatusBadge status={item.label} />
        </div>
      )}
      {item.citation && <CitationBadge sources={[item.citation]} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ContentCard · AG-04 recommendation snapshot
// ---------------------------------------------------------------------------

export function ContentCard({ item, testId }) {
  if (!item) return null;
  return (
    <div
      data-testid={testId || `content-${item.code || "item"}`}
      className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-3"
    >
      <div className="text-xs font-mono text-[var(--ag-text-dim)]">{item.code}</div>
      <div className="mt-0.5 text-sm font-medium">{item.title || item.name}</div>
      {item.reason && (
        <p className="mt-1 text-xs text-[var(--ag-text-dim)]">{item.reason}</p>
      )}
      {item.citation && <CitationBadge sources={[item.citation]} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CompetitorCard · AG-05 SWOT slot or gap entry
// ---------------------------------------------------------------------------

export function CompetitorCard({ title, items, tone = "info", citation, testId }) {
  return (
    <div
      data-testid={testId || `competitor-${title?.toLowerCase()}`}
      className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-3"
    >
      <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ag-text-dim)]">
        {title}
      </div>
      <ul className="mt-2 space-y-1">
        {(items || []).slice(0, 6).map((it, i) => {
          const text = typeof it === "string" ? it : (it.label || it.title || JSON.stringify(it));
          return (
            <li key={i} className="text-xs text-[var(--ag-text)]">
              · {text}
            </li>
          );
        })}
        {!items || items.length === 0 ? (
          <li className="text-xs text-[var(--ag-text-dim)]">Información insuficiente.</li>
        ) : null}
      </ul>
      {citation && <CitationBadge sources={[citation]} />}
    </div>
  );
}
